import NewTaskGroupButton from "@common-components/tasks-action-bar/add-task-group-button/new-task-group-button";
import { BoardRowType } from "@common-models/task";
import { Box, Stack } from "@mui/material";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { memo, useMemo } from "react";
import { DraggableProvided } from "react-beautiful-dnd";
import GroupHeader from "../group-header/GroupHeader";
import GroupHeaderBoardView from "../group-header/GroupHeaderBoardView";
import { BoardRowElement } from "../helpers/elements-factory/elementsFactory";
import NewTaskRow from "../new-task-row/new-task-row";
import TaskRow from "../task-row/task-row";
import classes from "./board-row.module.scss";

// Utility to check if drag should be allowed for this event target
const shouldAllowDrag = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) return true;
  
  // Check if the target is or is inside an interactive element
  const interactiveElement = target.closest(
    'button, a, input, textarea, select, [role="button"], [role="combobox"], [role="textbox"], [contenteditable="true"], [data-no-drag]'
  );
  
  return !interactiveElement;
};


interface BoardRowProps {
  item: BoardRowElement;
  provided: DraggableProvided | undefined;
  isDragging?: boolean;
  isClone?: boolean;
  isDisplayNewGroupButton?: boolean;
}

export function BoardRow({
  item,
  provided,
  isDragging,
  isClone,
  isDisplayNewGroupButton = true,
}: BoardRowProps) {
  const direction = useAppSelector((state) => state.UserReducer.direction);

  // Wrap drag handle props to filter interactive elements
  const filteredDragHandleProps = useMemo(() => {
    if (!provided?.dragHandleProps) return undefined;

    const originalProps: any = provided.dragHandleProps;
    
    // Wrap each event handler to check if drag should be allowed
    const wrapHandler = (handler: any) => {
      if (!handler) return undefined;
      return (event: any) => {
        if (shouldAllowDrag(event.target)) {
          handler(event);
        }
      };
    };

    return {
      ...originalProps,
      onMouseDown: wrapHandler(originalProps.onMouseDown),
      onTouchStart: wrapHandler(originalProps.onTouchStart),
      onKeyDown: wrapHandler(originalProps.onKeyDown),
    };
  }, [provided?.dragHandleProps]);

  if (isClone) {
    //console.log('Rendering clone!')
  }
  return (
    <Box
      data-testid={item.type === BoardRowType.task ? 'task-row'
        :
        item.type === BoardRowType.subTask ? 'subTask-row'
          :
          item.type === BoardRowType.groupHeader ? 'groupHeader-row'
            :
            'other-row'}
      sx={{
        // contain: 'layout style',
        direction: direction,
        "&:focus-visible": {
          outline: 'none!important'
        },

      }}
      {...provided?.draggableProps}
      ref={provided?.innerRef}
      style={provided?.draggableProps.style}
      className={`${classes.item} ${isDragging ? classes.isDragging : ""}`}>
      {
        item?.type === BoardRowType.groupHeader && provided &&
        <GroupHeader provided={provided} groupId={item?.groupId ?? ""} />
      }
      {
        item?.type === BoardRowType.boardHeader &&
        <GroupHeaderBoardView boardId={item?.boardId ?? ""} tasksCount={item.tasksCount ?? 0} />
      }

      {
        (item?.type === BoardRowType.task || item?.type === BoardRowType.subTask) &&
        <div
          {...filteredDragHandleProps}
          style={{ width: '100%' }}
        >
          <TaskRow
            taskId={item?.taskId ?? ""}
            isFirstTaskInGroup={item?.isFirstTaskInGroup ?? false}
            isLastTaskInGroup={item?.isLastTaskInGroup ?? false}
            colorAccent={item?.colorAccent ?? ""}
            isDragging={isDragging ?? false}
          />
        </div>
      }
      {
        item?.type === BoardRowType.newTask &&
        <Stack direction="row">
          <NewTaskRow
            groupId={item?.groupId ?? ""}
            parentTaskId={item?.parentTaskId}

            colorAccent={item?.colorAccent}
            isLastTaskInSubTasksGroup={item?.isLastTaskInSubTasksGroup}
          />

        </Stack>

      }

      {
        item?.type === BoardRowType.spacer &&
        <div style={{ backgroundColor: 'var(--color-bg)', height: '39px' }} >
          {/* {item?.groupId} */}
        </div>
      }

      {isDisplayNewGroupButton &&
        item?.type === BoardRowType.newGroup &&
        <Box sx={{ padding: "0 40px" }}> <NewTaskGroupButton position="bottom" /></Box>
      }




    </Box>
  );
}


export default memo(BoardRow);


